/**
 * Student Progress Viewer for Teachers
 * Monitor all students' progress in real-time
 */

import { useState } from 'react';
import { Search, Download, Filter, CheckCircle, Clock, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useExperimentStudents, useGradeDiscussion, useDiscussionQuestions } from '../utils/convexHooks';
import { Id } from '../convex/_generated/dataModel';

interface StudentProgressData {
    studentId: string;
    studentName: string;
    studentEmail: string;
    progressId?: Id<"studentProgress">;
    
    preLab: {
        attempts: number;
        score: number;
        passed: boolean;
        completedAt?: string;
    };
    
    labData: {
        startedAt?: string;
        completedAt?: string;
        manualEntry: boolean;
        dataPoints?: any[];
    };
    
    postLab: {
        submittedAt?: string;
        grade?: number;
        answers?: any[];
        teacherFeedback?: string;
    };
    
    status: 'not-started' | 'pre-lab' | 'in-lab' | 'post-lab' | 'completed' | 'teacher-bypass';
    lastActivity?: string;
}

interface StudentProgressViewerProps {
    experimentId: Id<"experimentConfigs">;
    experimentTitle: string;
    classCode: string;
}

export default function StudentProgressViewer({
    experimentId,
    experimentTitle,
    classCode,
}: StudentProgressViewerProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
    const gradeDiscussion = useGradeDiscussion();
    const [gradingState, setGradingState] = useState<{ [studentId: string]: boolean }>({});
    const [feedbackState, setFeedbackState] = useState<{ [studentId: string]: string }>({});
    const [gradeScoreState, setGradeScoreState] = useState<{ [studentId: string]: number | undefined }>({});
    
    const dbStudents = useExperimentStudents(experimentId);
    const discussionQuestionsList = useDiscussionQuestions(experimentId);
    
    const students: StudentProgressData[] = dbStudents ? dbStudents.map((studentWithProgress) => {
        const p = studentWithProgress.progress;
        
        const lastPreLabScore = p?.preLab.scores.length ? p.preLab.scores[p.preLab.scores.length - 1] : 0;
        
        return {
            studentId: studentWithProgress.studentId,
            studentName: studentWithProgress.studentName,
            studentEmail: studentWithProgress.studentEmail,
            progressId: p?._id,
            preLab: {
                attempts: p?.preLab.attempts || 0,
                score: lastPreLabScore,
                passed: !!p?.preLab.passed,
                completedAt: p?.preLab.completedAt ? new Date(p.preLab.completedAt).toISOString() : undefined,
            },
            labData: {
                startedAt: p?.labData?.startedAt ? new Date(p.labData.startedAt).toISOString() : undefined,
                completedAt: p?.labData?.completedAt ? new Date(p.labData.completedAt).toISOString() : undefined,
                manualEntry: p?.labData?.manualEntry || false,
                dataPoints: p?.labData?.dataPoints,
            },
            postLab: {
                submittedAt: p?.postLab?.answers.length && p.postLab.answers[0].submittedAt ? new Date(p.postLab.answers[0].submittedAt).toISOString() : undefined,
                grade: p?.postLab?.grade,
                answers: p?.postLab?.answers,
                teacherFeedback: p?.postLab?.teacherFeedback,
            },
            status: p?.status || 'not-started',
            lastActivity: p?.updatedAt ? new Date(p.updatedAt).toISOString() : new Date(studentWithProgress.joinedAt).toISOString(),
        };
    }) : [];

    if (dbStudents === undefined) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }
    
    const filteredStudents = students.filter((student) => {
        const matchesSearch = student.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            student.studentEmail.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = filterStatus === 'all' || student.status === filterStatus;
        return matchesSearch && matchesFilter;
    });
    
    const stats = {
        total: students.length,
        completed: students.filter(s => s.status === 'completed').length,
        inProgress: students.filter(s => ['pre-lab', 'in-lab', 'post-lab'].includes(s.status)).length,
        notStarted: students.filter(s => s.status === 'not-started').length,
    };
    
    const handleExport = () => {
        // TODO: Export to Excel/CSV
        console.log('Exporting student progress...');
    };

    const handleReviewClick = (studentId: string) => {
        setSelectedStudentId(studentId);
    };

    const handleSaveManualGrade = async (student: StudentProgressData) => {
        const gradeToSave = gradeScoreState[student.studentId] ?? student.postLab.grade;
        if (gradeToSave === undefined) {
            alert("Please enter a valid grade");
            return;
        }
        if (!student.progressId) {
            alert("No progress record found for this student");
            return;
        }
        setGradingState({ ...gradingState, [student.studentId]: true });
        
        try {
            await gradeDiscussion({
                progressId: student.progressId,
                grade: gradeToSave,
                feedback: feedbackState[student.studentId] ?? student.postLab.teacherFeedback ?? ""
            });
            // Update local state to reflect the save visually
            setSelectedStudentId(null);
        } catch (e) {
            console.error("Failed to grade", e);
            alert("Failed to save grade");
        } finally {
            setGradingState({ ...gradingState, [student.studentId]: false });
        }
    };

    const selectedStudent = students.find(s => s.studentId === selectedStudentId);

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
            {/* Header */}
            <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm p-6">
                <div className="w-full max-w-[1920px] lg:px-8 xl:px-12 mx-auto">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {experimentTitle}
                            </h1>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Class Code: <span className="font-mono font-bold text-indigo-600">{classCode}</span>
                            </p>
                        </div>
                        <button
                            onClick={handleExport}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                        >
                            <Download className="w-4 h-4" />
                            Export
                        </button>
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Total Students</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                            <p className="text-sm text-green-600 dark:text-green-400">Completed</p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.completed}</p>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                            <p className="text-sm text-blue-600 dark:text-blue-400">In Progress</p>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.inProgress}</p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400">Not Started</p>
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.notStarted}</p>
                        </div>
                    </div>
                </div>
            </header>
            
            <main className="w-full max-w-[1920px] lg:px-8 xl:px-12 mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Search and Filters */}
                <div className="mb-6 flex gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name or email..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-600"
                        />
                    </div>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    >
                        <option value="all">All Status</option>
                        <option value="not-started">Not Started</option>
                        <option value="pre-lab">Pre-Lab</option>
                        <option value="in-lab">In Lab</option>
                        <option value="post-lab">Discussion</option>
                        <option value="completed">Completed</option>
                    </select>
                </div>
                
                {/* Student List */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                    Student
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                    Pre-Lab
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                    Lab
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                    Discussion
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                    Grade
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                                    Last Activity
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredStudents.map((student) => (
                                <tr key={student.studentId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="px-6 py-4">
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {student.studentName}
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                {student.studentEmail}
                                            </p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {student.preLab.passed ? (
                                            <div className="flex items-center gap-2">
                                                <CheckCircle className="w-5 h-5 text-green-600" />
                                                <span className="text-sm text-gray-900 dark:text-white">
                                                    {student.preLab.score}%
                                                </span>
                                            </div>
                                        ) : student.preLab.attempts > 0 ? (
                                            <div className="flex items-center gap-2">
                                                <XCircle className="w-5 h-5 text-red-600" />
                                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                                    {student.preLab.score}% ({student.preLab.attempts} attempts)
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-400">Not started</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        {student.labData.completedAt ? (
                                            <CheckCircle className="w-5 h-5 text-green-600" />
                                        ) : student.labData.startedAt ? (
                                            <Clock className="w-5 h-5 text-blue-600" />
                                        ) : (
                                            <span className="text-sm text-gray-400">Not started</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        {student.postLab?.submittedAt ? (
                                            <div className="flex items-center gap-2">
                                                <CheckCircle className="w-5 h-5 text-green-600" />
                                                <span className="text-sm text-gray-500">Submitted</span>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-gray-400">Not submitted</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        {student.postLab?.grade !== undefined ? (
                                            <button 
                                                onClick={() => handleReviewClick(student.studentId)}
                                                className="interactable-btn text-sm font-bold text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-colors border border-gray-200 dark:text-white dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600"
                                            >
                                                {student.postLab.grade}%
                                            </button>
                                        ) : student.postLab?.submittedAt ? (
                                            <button 
                                                onClick={() => handleReviewClick(student.studentId)}
                                                className="interactable-btn text-xs font-bold text-indigo-700 bg-indigo-100 hover:bg-indigo-200 px-3 py-1.5 rounded-lg transition-colors border border-indigo-200"
                                            >
                                                Needs Review
                                            </button> 
                                        ) : (
                                            <span className="text-sm text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <StatusBadge status={student.status} needsManualReview={student.status === 'completed' && student.postLab.grade === undefined && student.postLab.answers && student.postLab.answers.length > 0} />
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                        {student.lastActivity ? new Date(student.lastActivity).toLocaleString() : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {filteredStudents.length === 0 && (
                        <div className="text-center py-12">
                            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600 dark:text-gray-400">No students found</p>
                        </div>
                    )}
                </div>
            </main>

            {/* Manual Review Modal */}
            {selectedStudent && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white dark:bg-gray-800 border items-start border-gray-200 dark:border-gray-700 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                            <div>
                                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Review Student: {selectedStudent.studentName}</h2>
                            </div>
                            <button onClick={() => setSelectedStudentId(null)} className="p-1 px-3 text-red-500 bg-red-100 hover:bg-red-200 rounded text-sm font-bold ml-4">Close</button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto flex-1 space-y-4">
                            {/* Score Breakdown (Lab Data) */}
                            {selectedStudent.labData.dataPoints && selectedStudent.labData.dataPoints.length > 0 && selectedStudent.labData.dataPoints[0].breakdown && (
                                <div className="mb-6">
                                    <h3 className="font-bold text-indigo-600 uppercase text-xs mb-3">Lab Data Score Breakdown</h3>
                                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm space-y-2">
                                        <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                                            <span>Concentration Accuracy</span>
                                            <span className="font-bold">{selectedStudent.labData.dataPoints[0].breakdown.concentrationAccuracy} / 40</span>
                                        </div>
                                        <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                                            <span>Data Quality</span>
                                            <span className="font-bold">{selectedStudent.labData.dataPoints[0].breakdown.dataQuality} / 30</span>
                                        </div>
                                        <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
                                            <span>Technique</span>
                                            <span className="font-bold">{selectedStudent.labData.dataPoints[0].breakdown.techniqueScore} / 20</span>
                                        </div>
                                        <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-2">
                                            <span>Time Bonus</span>
                                            <span className="font-bold">{selectedStudent.labData.dataPoints[0].breakdown.timeBonus} / 10</span>
                                        </div>
                                        <div className="flex justify-between text-sm font-bold text-indigo-700 dark:text-indigo-400 pt-1">
                                            <span>Total (Simulated Lab)</span>
                                            <span>{selectedStudent.labData.dataPoints[0].algorithmicScore} / 100</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <h3 className="font-bold text-indigo-600 uppercase text-xs">Discussion Answers</h3>
                            {(!selectedStudent.postLab.answers || selectedStudent.postLab.answers.length === 0) && (
                                <p className="text-sm text-gray-500">Student submitted no discussion answers.</p>
                            )}
                            
                            {selectedStudent.postLab.answers?.map((ans, i) => {
                                const questionText = discussionQuestionsList?.find(q => q._id === ans.questionId)?.question || ans.questionId;
                                return (
                                <div key={i} className="mb-4 bg-gray-50 dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-3">{"Q: " + questionText}</p>
                                    <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-300 dark:border-gray-600 mb-3">
                                        <p className="text-sm text-gray-900 dark:text-gray-100">{ans.answer || <span className="text-gray-400 italic">No answer provided</span>}</p>
                                    </div>
                                    {ans.autoGradeScore !== undefined && (
                                        <div className="text-xs space-y-1 bg-indigo-50 border border-indigo-100 dark:bg-indigo-900/30 dark:border-indigo-800 p-2 rounded">
                                            <p className="font-medium text-indigo-800 dark:text-indigo-300 flex items-center justify-between">
                                                AI Score: <span>{ans.autoGradeScore}%</span>
                                            </p>
                                            {ans.autoGradeFeedback && <p className="text-indigo-700/80 dark:text-indigo-400 mt-1">{ans.autoGradeFeedback}</p>}
                                        </div>
                                    )}
                                </div>
                            )})}

                            <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700 mt-6">
                                <h3 className="font-bold text-indigo-600 uppercase text-xs mb-1">Final Grade Override</h3>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Overall Discussion Grade (0-100%)</label>
                                    <input 
                                        type="number" min="0" max="100"
                                        placeholder={selectedStudent.postLab.grade?.toString() || "Enter grade 0-100"}
                                        value={gradeScoreState[selectedStudent.studentId] ?? selectedStudent.postLab.grade ?? ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setGradeScoreState({ 
                                                ...gradeScoreState, 
                                                [selectedStudent.studentId]: val === '' ? undefined : parseInt(val)
                                            });
                                        }}
                                        className="w-32 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teacher Feedback Notes</label>
                                    <textarea 
                                        rows={3}
                                        value={feedbackState[selectedStudent.studentId] ?? selectedStudent.postLab.teacherFeedback ?? ''}
                                        placeholder="Provide any feedback for the student..."
                                        onChange={(e) => setFeedbackState({ ...feedbackState, [selectedStudent.studentId]: e.target.value })}
                                        className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                                    ></textarea>
                                </div>

                                <div className="mt-4 flex justify-end">
                                    <button 
                                        onClick={() => handleSaveManualGrade(selectedStudent)}
                                        disabled={gradingState[selectedStudent.studentId] || (gradeScoreState[selectedStudent.studentId] ?? selectedStudent.postLab.grade) === undefined}
                                        className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg shadow-sm font-medium"
                                    >
                                        {gradingState[selectedStudent.studentId] ? "Saving..." : "Save Grade & Close"}
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}

function StatusBadge({ status, needsManualReview }: { status: string, needsManualReview?: boolean }) {
    const configs = {
        'not-started': { color: 'gray', text: 'Not Started' },
        'pre-lab': { color: 'yellow', text: 'Pre-Lab' },
        'in-lab': { color: 'blue', text: 'In Lab' },
        'post-lab': { color: 'purple', text: 'Discussion' },
        'completed': { color: 'green', text: 'Completed' },
        'teacher-bypass': { color: 'orange', text: 'Teacher Bypass' },
    };
    
    const config = configs[status as keyof typeof configs] || configs['not-started'];
    if (needsManualReview) {
        config.text = 'Needs Manual Review';
        config.color = 'yellow';
    }
    
    const colorClasses = {
        gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
        yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    };
    
    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            colorClasses[config.color as keyof typeof colorClasses]
        }`}>
            {config.text}
        </span>
    );
}
